import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@rabbit/olive/context/UserContext';
import { Button } from '@rabbit/elements/shared-components';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { getGreeting } from '@rabbit/bizproc/react';
import { useMediaQuery } from '@rabbit/olive/hooks/useMediaQuery';
import ROUTE_NAME from '@rabbit/olive/utils/url-constants';
import logoiWarranty from '@rabbit/olive/assets/images/logo/logo-iwarranty-black.svg';
import styles from './PozieTemplate.module.css';
import Footer from '@rabbit/olive/components/molecules/Footer/Footer';
import { useMyConsumerPersona } from '@rabbit/data/portal';

const items = [
  {
    id: 1,
    title: 'Warranties at your fingertips',
    description:
      'No trying to find old receipts, remembering phone calls or finding emails. Everything in one place. Easy and convenient.',
    border: true,
    Icon: PlusIcon,
  },
  {
    id: 2,
    title: 'Easy claim process',
    description:
      'Need to make a claim. Easy! Log into the portal where you registered your product. Enter fault details and the process is underway.',
    border: true,
    Icon: PlusIcon,
  },
  {
    id: 3,
    title: 'Free to use',
    description:
      'Experience peace of mind in managing warranties with no service fees.',
    border: false,
    Icon: PlusIcon,
  },
];

function PozieTemplate({
  onClickRegisterProduct,
}: {
  onClickRegisterProduct?: () => void;
}) {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const consumerPersona = useMyConsumerPersona();
  const consumerPersonaData = consumerPersona?.data.consumer_private;
  const [selected, setSelected] = useState(null);

  const handleToggleDescription = (itemId: any) => {
    setSelected((prevSelected) => (prevSelected === itemId ? null : itemId));
  };

  return (
    <>
      <main
        className={`${styles['font-inter']} mx-auto max-w-[1326px] px-4 pb-14 text-gray-700 lg:pb-24`}
      >
        {/* header */}
        <header className="flex flex-wrap items-center gap-6 pt-10 pb-14">
          <div className="mr-auto flex gap-2 sm:gap-4">
            <img
              src="https://mcusercontent.com/46d498bc614816f815d16c07b/images/d8ffd4f1-6934-4e7f-c60e-f2c274b07ada.png"
              alt="POZIE"
              className="xs:h-8 h-6 lg:h-10"
            />
            <span className="border-r-2 border-gray-300" />
            <img
              src={logoiWarranty}
              alt="iWarranty"
              className="xs:h-8 h-6 lg:h-10"
            />
          </div>

          {!consumerPersonaData ? (
            <div className="inline-flex">
              <Button
                kind="primary"
                size="md"
                onClick={() => navigate(ROUTE_NAME.NON_LOGGED_CONTINUE_CLAIM)}
                className={`${styles['font-inter']} lg:min-w-[100px min-w-[80px] font-semibold`}
              >
                Login
              </Button>
            </div>
          ) : (
            <p className="text-lg font-bold text-gray-900">
              Hi{' '}
              {consumerPersonaData?.splitname?.first ||
                consumerPersonaData?.fullname}
              , good {getGreeting()}.
            </p>
          )}
        </header>

        {/* hero-image */}
        <section
          className="relative flex aspect-[3/4] w-full flex-col justify-end overflow-hidden rounded-lg bg-cover bg-no-repeat p-8 sm:aspect-[16/6] sm:items-end sm:justify-center"
          style={{
            backgroundImage: `url(${
              isDesktop
                ? 'https://mcusercontent.com/46d498bc614816f815d16c07b/images/2cbc0a09-7232-3982-eb27-8ac12f065a59.jpg'
                : 'https://mcusercontent.com/46d498bc614816f815d16c07b/images/0c9f4ed5-bcea-d9e0-cc1f-d2b5b397ae8d.jpg'
            })`,
          }}
        >
          <div className="flex flex-col items-center gap-6 text-white sm:w-3/4 sm:flex-row sm:justify-evenly lg:gap-10">
            <div className="flex-shrink-0">
              <img
                className="w-10 sm:w-20 xl:w-28"
                src="https://mcusercontent.com/46d498bc614816f815d16c07b/images/e103c029-479a-327d-7a0f-402ac21adc56.png"
                alt="POZIE"
              />
            </div>
            <div className="flex flex-col space-y-6 sm:w-[62%]">
              <h1 className="text-2xl font-semibold leading-[1.2] lg:text-[36px] xl:text-[52px]">
                Register your POZIE product today
              </h1>
              <p className="text-lg lg:text-2xl">
                POZIE has partnered with iWarranty to give you convenience and
                peace of mind with your purchase.
              </p>
            </div>
          </div>
        </section>

        {/* register-cta */}
        <section className="py-14 text-center">
          <Button
            kind="primary"
            onClick={onClickRegisterProduct}
            className={`${styles['font-inter']} inline-flex min-h-[56px] min-w-full text-lg sm:min-w-[240px]`}
          >
            Register your product
          </Button>
        </section>

        {/* meta-text */}
        <p className="mb-14 text-lg lg:text-xl">
          At POZIE, we want you, our customer, to have a great experience when
          buying and using our products. Our mission is to make pool and spa
          equipment that is engineered to clean and ownership made easy. We have
          partnered with iWarranty to ensure that should you have a problem with
          a POZIE product, you have peace of mind that there is an easy, quick
          and fair process to resolve any problems. This means you can purchase
          any of our products with peace of mind knowing that we’ve got your
          back.
        </p>

        {/* experience section */}
        <section className="mb-14 flex flex-col-reverse gap-14 sm:flex-row sm:items-center sm:justify-between lg:py-4">
          <div className="w-full xl:w-2/3">
            <h2 className="mb-10 text-2xl font-semibold leading-[1.2] text-gray-900  lg:text-[36px] xl:text-[48px]">
              How can POZIE and iWarranty provide you the best post-purchase
              experience ?
            </h2>

            <p className="text-lg lg:text-xl">
              Within the POZIE iWarranty platform you can store, track and
              manage all your POZIE products in one place. Accessible from
              anywhere, on any device, at any time.
            </p>
          </div>
          <div className="w-full sm:w-1/3">
            <img
              src="https://mcusercontent.com/46d498bc614816f815d16c07b/images/8604f693-d7ba-b08a-52f6-198d7c244bd5.jpg"
              className="rounded-lg"
              alt="Lifestyle"
            />
          </div>
        </section>

        {/* accordion section */}
        <section className="flex flex-col items-center gap-14 pb-14 lg:flex-row xl:mb-14 xl:items-start xl:pt-14">
          <ul className="w-full lg:w-[54%] lg:flex-shrink-0">
            {items.map((item) => (
              <li key={item.id} className="border-b-2 last:border-b-0">
                <button
                  className="inline-flex w-full items-center justify-between gap-6 py-6 text-left text-gray-900"
                  onClick={() => handleToggleDescription(item.id)}
                >
                  <span className="text-2xl font-semibold leading-[1.2] lg:text-[36px] xl:text-[48px]">
                    {item.title}
                  </span>

                  {selected === item.id ? (
                    <MinusIcon className="w-8" />
                  ) : (
                    <PlusIcon className="w-8" />
                  )}
                </button>

                {selected === item.id && (
                  <p className="mt-2 mb-8 text-lg lg:text-xl">
                    {item.description}
                  </p>
                )}
              </li>
            ))}
          </ul>

          <div className="w-full text-center xl:relative">
            <img
              src="https://mcusercontent.com/46d498bc614816f815d16c07b/images/3ffcc07a-422d-c7d1-3a6c-a72f11b80636.png"
              alt="iWarranty App"
              className="inline-block xl:absolute xl:top-[-50px] xl:left-[50px] xl:max-w-[820px]"
            />
          </div>
        </section>

        {/* contact info */}
        <section className="flex flex-col">
          <h3 className="mb-1 text-lg font-semibold text-gray-900">
            POZIE service team
          </h3>
          <p className="mb-8 text-gray-700">
            Please reach out to us below if you have any specific questions.
          </p>

          <div className="flex flex-col gap-3 text-lg sm:flex-row sm:gap-8">
            <ul className="list-none space-y-3">
              <li>
                Email:{' '}
                <a className="text-gray-700" href="mailto:support@pozie.com.au">
                  support@pozie.com.au
                </a>
              </li>
              <li>
                Helpline:{' '}
                <a className="text-gray-700" href="tel:1300 118 632">
                  1300 118 632
                </a>
              </li>
            </ul>
          </div>
        </section>
      </main>

      {/* footer */}
      <Footer className={`${styles['font-inter']}`} />
    </>
  );
}

export default PozieTemplate;
